import React, { useState } from "react";
import { LayoutGrid, Eye } from "lucide-react";
import { Button } from "@/components/Calendar/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/Calendar/ui/popover";
import { Label } from "@/components/Calendar/ui/label";
import { Separator } from "@/components/Calendar/ui/separator";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/Calendar/ui/radio-group";

interface ViewSettingsProps {
  isDarkMode: boolean;
  view: "month" | "week" | "day" | "threeday" | "workweek";
  onViewChange: (
    view: "month" | "week" | "day" | "threeday" | "workweek",
  ) => void;
  columnMode: "practitioner" | "day" | "services" | "room" | "day-practitioner";
  onColumnModeChange: (
    mode: "practitioner" | "day" | "services" | "room" | "day-practitioner",
  ) => void;
  displayMode: "calendar" | "list";
  onDisplayModeChange: (mode: "calendar" | "list") => void;
  showColumnMode?: boolean;
}

export function ViewSettings({
  isDarkMode,
  view,
  onViewChange,
  columnMode,
  onColumnModeChange,
  displayMode,
  onDisplayModeChange,
  showColumnMode = false,
}: ViewSettingsProps) {
  const [open, setOpen] = useState(false);

  const getViewLabel = () => {
    switch (view) {
      case "day":
        return "Day";
      case "threeday":
        return "3 Day";
      case "workweek":
        return "Work Week";
      case "week":
        return "Week";
      case "month":
        return "Month";
      default:
        return view;
    }
  };

  const getDisplayModeLabel = () => {
    return displayMode === "calendar" ? "Calendar" : "List";
  };

  const getColumnModeLabel = () => {
    switch (columnMode) {
      case "practitioner":
        return "Practitioner";
      case "day":
        return "Day";
      case "services":
        return "Services";
      case "room":
        return "Room";
      case "day-practitioner":
        return "Day by Practitioner";
      default:
        return columnMode;
    }
  };

  const getButtonText = () => {
    const viewText = getViewLabel();
    // Show group by only for day/week views in calendar mode
    if (showColumnMode) {
      return `${viewText} • ${getColumnModeLabel()}`;
    }
    return viewText;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`relative ${
            isDarkMode
              ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
              : "bg-white border-gray-300 hover:bg-gray-50"
          }`}
        >
          <Eye className="w-4 h-4 mr-2" />
          {getButtonText()}
        </Button>
      </PopoverTrigger>
      {/* Height capped to the space Radix measures around the trigger, so it
          scrolls instead of running off-screen on phones */}
      <PopoverContent
        align="end"
        collisionPadding={12}
        className={`w-[calc(100vw-1.5rem)] sm:w-[320px] p-4 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overscroll-contain ${
          isDarkMode
            ? "bg-gray-800 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3
              className={`text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              View Settings
            </h3>
          </div>

          <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />

          {/* Display Mode */}
          <div className="space-y-3">
            <Label
              className={`text-xs font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              Display Mode
            </Label>
            <RadioGroup
              value={displayMode}
              onValueChange={(value) =>
                onDisplayModeChange(value as "calendar" | "list")
              }
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="calendar" id="display-calendar" />
                <Label
                  htmlFor="display-calendar"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4" />
                    Calendar
                  </div>
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="list" id="display-list" />
                <Label
                  htmlFor="display-list"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <LayoutGrid className="w-4 h-4" />
                    List
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <Separator className={isDarkMode ? "bg-gray-700" : "bg-gray-200"} />

          {/* View Type */}
          <div className="space-y-3">
            <Label
              className={`text-xs font-medium ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              View Type
            </Label>
            <RadioGroup
              value={view}
              onValueChange={(value) => onViewChange(value as any)}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="day" id="view-day" />
                <Label
                  htmlFor="view-day"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Day
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="threeday" id="view-threeday" />
                <Label
                  htmlFor="view-threeday"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Three Day
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="workweek" id="view-workweek" />
                <Label
                  htmlFor="view-workweek"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Work Week
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="week" id="view-week" />
                <Label
                  htmlFor="view-week"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Week
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="month" id="view-month" />
                <Label
                  htmlFor="view-month"
                  className={`cursor-pointer text-sm ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Month
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Group By - Only show for day/week views in calendar mode */}
          {showColumnMode && (
            <>
              <Separator
                className={isDarkMode ? "bg-gray-700" : "bg-gray-200"}
              />
              <div className="space-y-3">
                <Label
                  className={`text-xs font-medium ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Group By
                </Label>
                <RadioGroup
                  value={columnMode}
                  onValueChange={(value) => onColumnModeChange(value as any)}
                  className="space-y-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="practitioner"
                      id="group-practitioner"
                    />
                    <Label
                      htmlFor="group-practitioner"
                      className={`cursor-pointer text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Practitioner
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="day" id="group-day" />
                    <Label
                      htmlFor="group-day"
                      className={`cursor-pointer text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Day
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="day-practitioner"
                      id="group-day-practitioner"
                    />
                    <Label
                      htmlFor="group-day-practitioner"
                      className={`cursor-pointer text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Day by Practitioner
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="services" id="group-services" />
                    <Label
                      htmlFor="group-services"
                      className={`cursor-pointer text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Services
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="room" id="group-room" />
                    <Label
                      htmlFor="group-room"
                      className={`cursor-pointer text-sm ${
                        isDarkMode ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Room
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
